import { GetServerSidePropsContext } from "next";
import { SnakeToCamelCaseNested } from "@literati/kids-api";

import { mapKeysSnakeCase } from "utils";
import {
  ParsedErrorResponse,
  requestJSON,
  requestJSONBasic,
} from "lib/api/base";
import { BookPreference, BookSchemaCamelCase } from "lib/api/onboarding";

export type { BookPreference, BookSchemaCamelCase };

export const BASE_URL = "/api/onboarding";

export enum Archetype {
  TOP_PICK = "top-pick",
  POPULAR = "popular",
  FEATURED = "featured",
  READING_CHALLENGE = "reading-challenge",
  BEDTIME = "bedtime",
  CLASSIC = "classic",
  EXCLUSIVE = "exclusive",
  HIDDEN_GEM = "hidden-gem",
}

export interface Recommendation {
  book: BookSchemaCamelCase;
  archetype: Archetype;
}

interface BookShelfQuizRecommendationsResponseSchema {
  recommendations: Recommendation[];
  quizId: string;
}

interface GetBookShelfQuizBooksParams {
  readingAge: number;
  readingLevelAdjustment: number;
  ctx?: GetServerSidePropsContext;
}
/**
 * Gets the initial book shelf quiz books
 * to be used in the book shuffle step.
 */
export async function getBookShelfQuizBooks(
  params: GetBookShelfQuizBooksParams
): Promise<BookSchemaCamelCase[] | ParsedErrorResponse> {
  const { readingAge, readingLevelAdjustment, ctx } = params;

  const url = `${BASE_URL}/book-shelf-quiz-books`;
  const payload = mapKeysSnakeCase({ readingAge, readingLevelAdjustment });
  return await requestJSON(url, payload, "GET", { ctx });
}

interface PostBookShelfQuizPreferencesParams {
  bookPreferences: BookPreference[];
  readingAge: number;
  readingLevelAdjustment: number;
  ctx?: GetServerSidePropsContext;
}
/**
 * This endpoint is used to submit the user's book shelf quiz preferences
 * from the book shuffle step.
 *
 * This step returns a list of recommendation books for the user
 * and the quizId to re-retrieve the recommendations
 */
export async function postBookShelfQuizPreferences(
  params: PostBookShelfQuizPreferencesParams
): Promise<
  | ParsedErrorResponse
  | SnakeToCamelCaseNested<BookShelfQuizRecommendationsResponseSchema>
> {
  const { bookPreferences, readingAge, readingLevelAdjustment, ctx } = params;

  const payload = mapKeysSnakeCase({
    bookPreferences: bookPreferences.map(mapKeysSnakeCase),
    readingAge,
    readingLevelAdjustment,
  });

  const url = `${BASE_URL}/book-shelf-quiz-submit-preferences`;
  return await requestJSONBasic(url, payload, "POST", { ctx });
}

interface GetBookShelfQuizRecommendationsParams {
  quizId: string;
  ctx?: GetServerSidePropsContext;
}
/**
 * Get the recommendation books for the given quizId
 */
export async function getBookShelfQuizRecommendations(
  params: GetBookShelfQuizRecommendationsParams
): Promise<
  | ParsedErrorResponse
  | SnakeToCamelCaseNested<BookShelfQuizRecommendationsResponseSchema>
> {
  const { quizId, ctx } = params;

  const url = `${BASE_URL}/book-shelf-quiz-recommendations`;
  const payload = mapKeysSnakeCase({ quizId });
  return await requestJSON(url, payload, "GET", { ctx });
}

interface PostBookShelfQuizRecommendationPreferencesParams {
  bookPreferences: BookPreference[];
  quizId: string;
  ctx?: GetServerSidePropsContext;
}
interface PostBookShelfQuizRecommendationPreferencesResponse {
  success: boolean;
}
/**
 * This endpoint is used to submit the user's preferences
 * on the recommendation results. Indicating if they want to
 * include them in their welcome box or not.
 */
export async function postBookShelfQuizRecommendationPreferences(
  params: PostBookShelfQuizRecommendationPreferencesParams
): Promise<
  ParsedErrorResponse | PostBookShelfQuizRecommendationPreferencesResponse
> {
  const { bookPreferences, quizId, ctx } = params;

  const payload = mapKeysSnakeCase({
    bookPreferences: bookPreferences.map(mapKeysSnakeCase),
    quizId,
  });

  const url = `${BASE_URL}/book-shelf-recommendation-preferences`;
  return await requestJSONBasic(url, payload, "POST", { ctx });
}
